import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { PageContainer } from '../../../../../components/page-container/page-container'
import { EyeView } from '../../../../../components/icons/eye-view'
import { Loading } from '../../../../../components/loading/loading'
import { Error } from '../../../../../components/error/error'
import { FilterModal } from '../../../../../components/filter-modal/filter-modal-new'
import { paths } from '../../../../../router/paths'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../hooks/redux.hooks'
import { styles, texts } from './daily-sales.styles'
import {
  clearError,
  fetchCollectionOrdersPaged,
} from '../../../../../store/daily-sale/collection-order.slice'
import {
  mapCollectionOrderStatus,
  formatCurrencyGTQ,
  formatTimeString,
} from '../../../../../utils/formatters'
import { PageSizeSelect } from '../../../../../components/page-size-select/page-size-select'
import { PrintReceiptModal } from '../../../../../components/print-receipt-modal/print-receipt-modal'
import { fetchDailySalesReceiptContent } from '../../../../../services/daily-sales-print.service'
import { Button } from '../../../../../components/button'

// Interface de filtros alinhada ao FilterModal (status como string e datas se necessário)
interface FilterOptions {
  status: string
  startDate: string
  endDate: string
  token?: string
}

export const DailySales = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { collectionOrders, loading, error, pagination } = useAppSelector(
    (state: any) => state.collectionOrder,
  )

  // Ajuste filtros p/ integrar modal (status default '4' = todos)
  const [filters, setFilters] = useState<FilterOptions>({
    status: '-1',
    startDate: '',
    endDate: '',
    token: '',
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [printOpen, setPrintOpen] = useState(false)
  const isInitializedRef = useRef(false)
  const [hydrated, setHydrated] = useState(false)

  // Chaves de storage
  const STORAGE_KEY = 'dailySalesState'

  // Restaura estado inicial SOMENTE se veio de detalhe (flag) ou há query params
  useEffect(() => {
    if (isInitializedRef.current) return

    const searchParams = new URLSearchParams(location.search)
    const preserve = sessionStorage.getItem('dailySalesPreserve') === '1'
    const urlStatus = searchParams.get('status')
    const urlStart = searchParams.get('startDate')
    const urlEnd = searchParams.get('endDate')
    const urlToken = searchParams.get('token')
    const urlPage = searchParams.get('page')
    const urlPageSize = searchParams.get('pageSize')

    let restored = false
    const nextFilters = { ...filters }

    if (urlStatus !== null) {
      nextFilters.status = urlStatus
      restored = true
    }
    if (urlStart !== null) {
      nextFilters.startDate = urlStart
      restored = true
    }
    if (urlEnd !== null) {
      nextFilters.endDate = urlEnd
      restored = true
    }
    if (urlToken !== null) {
      nextFilters.token = urlToken
      restored = true
    }

    if (restored) setFilters(nextFilters)
    if (urlPage) setCurrentPage(Number(urlPage))
    if (urlPageSize) setPageSize(Number(urlPageSize))

    if (!restored && !urlPage && !urlPageSize) {
      if (preserve) {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY)
          if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed.filters) setFilters(parsed.filters)
            if (parsed.currentPage) setCurrentPage(parsed.currentPage)
            if (parsed.pageSize) setPageSize(parsed.pageSize)
          }
        } catch {
          /* silent */
        }
      } else {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }

    isInitializedRef.current = true
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!isInitializedRef.current || !hydrated) return

    const params = new URLSearchParams()

    if (filters.status && filters.status !== '-1')
      params.set('status', filters.status)
    if (filters.startDate) params.set('startDate', filters.startDate)
    if (filters.endDate) params.set('endDate', filters.endDate)
    if (filters.token) params.set('token', filters.token)
    if (currentPage > 1) params.set('page', String(currentPage))
    if (pageSize !== 10) params.set('pageSize', String(pageSize))

    const newSearch = params.toString()
    if (newSearch !== location.search.replace(/^\?/, '')) {
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true },
      )
    }

    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ filters, currentPage, pageSize }),
    )
    loadData()
  }, [
    dispatch,
    currentPage,
    filters,
    pageSize,
    location.pathname,
    location.search,
    navigate,
    hydrated,
  ])

  const loadData = () => {
    let startDateToSend: string | undefined = filters.startDate || undefined
    let endDateToSend: string | undefined = filters.endDate || undefined

    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]

    if (startDateToSend && !endDateToSend) {
      endDateToSend = startDateToSend
    } else if (!startDateToSend && endDateToSend) {
      startDateToSend = todayStr
    }

    // Para status '-1' (todos), não enviamos status para buscar tudo do backend
    // A filtragem para mostrar apenas 1, 2, 3, 4 será feita no frontend
    dispatch(
      fetchCollectionOrdersPaged({
        pageIndex: currentPage - 1, // Convertendo para 0-based index
        pageSize,
        status:
          filters.status.toString() === '-1'
            ? undefined
            : Number(filters.status),
        startDate: startDateToSend,
        endDate: endDateToSend,
        token: filters.token?.trim() ? filters.token.trim() : undefined,
      }),
    )
  }

  const handleGoBack = () => {
    // Saindo para dashboard => não preservar próxima entrada
    sessionStorage.removeItem('dailySalesPreserve')
    navigate(paths.home)
  }

  const handlePrintList = () => {
    if (!collectionOrders || collectionOrders.length === 0) return
    setPrintOpen(true)
  }

  const handleViewSale = (collectionOrderId: number) => {
    const order = filteredCollectionOrders.find(
      (o: any) => o.CollectionOrderId === collectionOrderId,
    )
    sessionStorage.setItem('dailySalesPreserve', '1')
    navigate(
      paths.collectionOrderDetail.replace(':id', String(collectionOrderId)),
      { state: { collectionOrder: order } },
    )
  }

  const handleRetry = () => {
    dispatch(clearError())
    loadData()
  }

  const getStatusText = (status: number) => mapCollectionOrderStatus(status)

  const getStatusStyle = (status: number): string => {
    if (status === 1) return styles.statusAwaitingPayment
    if (status === 2) return styles.statusSettled
    if (status === 3) return styles.statusCanceledRefund
    if (status === 4) return styles.statusExpired

    return styles.statusError
  }

  const handleApplyFilters = (newFilters: FilterOptions) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset para primeira página
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    return () => {
      const preserve = sessionStorage.getItem('dailySalesPreserve') === '1'
      if (!preserve) {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [])

  const formatTime = (dateString: string) => formatTimeString(dateString)
  const formatCurrency = (amount: number) => formatCurrencyGTQ(amount)

  // Filtrar dados para mostrar apenas status 1, 2, 3 quando filter status é '-1'
  const filteredCollectionOrders =
    filters.status.toString() === '-1'
      ? collectionOrders.filter((order: any) =>
          [1, 2, 3, 4].includes(order.Status),
        )
      : collectionOrders

  return (
    <PageContainer type="authenticated" title={texts.title}>
      <div className={styles.container}>
        {/* Filtros */}
        <div className={styles.filtersContainer}>
          <button
            className={styles.filterButton}
            onClick={() => setIsFilterModalOpen(true)}
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {texts.filter}
          </button>
          <div className="ml-4 flex items-center gap-2">
            <PageSizeSelect
              value={pageSize}
              onChange={val => {
                setPageSize(val)
                setCurrentPage(1)
              }}
              label={texts.items}
              options={[10, 20, 30, 50, 75, 100]}
            />
          </div>
        </div>

        {/* Loading */}
        {loading && <Loading text="Cargando..." />}
        {/* Error */}
        {error && <Error message={error} onRetry={handleRetry} />}

        {/* Conteúdo Principal */}
        {!loading && !error && (
          <>
            {!filteredCollectionOrders ||
            filteredCollectionOrders.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <svg
                    className="h-16 w-16 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className={styles.emptyStateTitle}>{texts.noRecord}</h3>
                <p className={styles.emptyStateMessage}>{texts.tryAgain}</p>
                <div className={styles.emptyStateActions}>
                  <button
                    className={styles.emptyStateButton}
                    onClick={() => setIsFilterModalOpen(true)}
                  >
                    Filtrar
                  </button>
                  <button
                    className={styles.emptyStateButtonSecondary}
                    onClick={handleRetry}
                  >
                    {texts.updateFilter}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Cards (mobile e responsivo) similar aos de Previous Closings */}
                <div className={styles.cardsContainer}>
                  {filteredCollectionOrders.map((order: any) => (
                    <div key={order.CollectionOrderId} className={styles.card}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardDate}>
                          Token: {order.Token}
                        </div>
                        <div className={styles.cardTime}>
                          Hora: {formatTime(order.CreationDate)}
                        </div>
                      </div>
                      <div className={styles.cardBody}>
                        <div className={styles.cardRow}>
                          <span className={styles.cardLabel}>Status:</span>
                          <span className={getStatusStyle(order.Status)}>
                            {getStatusText(order.Status)}
                          </span>
                        </div>
                        <div className={styles.cardRow}>
                          <span className={styles.cardLabel}>Monto:</span>
                          <span className={styles.cardAmount}>
                            {formatCurrency(order.PrincipalValue)}
                          </span>
                        </div>
                        <div className={styles.cardActions}>
                          <button
                            className={styles.actionIconButton}
                            onClick={() =>
                              handleViewSale(order.CollectionOrderId)
                            }
                            title="Ver detalles"
                          >
                            <EyeView width={20} height={20} color="#6B7280" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Paginação */}
        {pagination && pagination.totalPages > 1 && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationControls}>
              {/* Ir para primeira página */}
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1}
                title="Primera página"
              >
                <svg
                  className={styles.paginationButtonIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 19l-7-7 7-7M19 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                title="Página anterior"
              >
                <svg
                  className={styles.paginationButtonIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .slice(0, 5)
                .map(page => (
                  <button
                    key={page}
                    className={
                      page === pagination.currentPage
                        ? styles.paginationButtonActive
                        : styles.paginationButton
                    }
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                title="Próxima página"
              >
                <svg
                  className={styles.paginationButtonIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              {/* Ir para última página */}
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                title="Última página"
              >
                <svg
                  className={styles.paginationButtonIcon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 5l7 7-7 7M5 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Botões de Ação */}
        <div className={styles.actionsContainer}>
          <Button
            className={`${styles.printButton} ${
              !collectionOrders || collectionOrders.length === 0
                ? 'cursor-not-allowed opacity-50'
                : ''
            }`}
            disabled={!collectionOrders || collectionOrders.length === 0}
            onClick={handlePrintList}
          >
            {texts.print}
          </Button>
          <Button className={styles.backButton} onClick={handleGoBack}>
            {texts.goBack}
          </Button>
        </div>
      </div>

      {/* Modal de Filtro */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
        enableTokenField
        statusOptions={[
          { value: '-1', label: 'Todos los estados' },
          { value: '1', label: 'Pendiente de pago' },
          { value: '2', label: 'Liquidado' },
          { value: '3', label: 'Devuelto' },
          { value: '4', label: 'Venció' },
        ]}
        defaultStatusValue="-1"
        statusLabel="Status"
      />

      <PrintReceiptModal
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        type="daily-sales"
        data={{
          filters,
          pageIndex: currentPage - 1,
          pageSize,
        }}
        fetchOverride={async mode => {
          // Normaliza datas para manter o mesmo critério da listagem
          let startDate = filters.startDate || undefined
          let endDate = filters.endDate || undefined
          if (startDate && !endDate) endDate = startDate
          if (!startDate && endDate) {
            // Se só veio endDate, usar hoje como start (mesma lógica da listagem)
            const today = new Date().toISOString().split('T')[0]
            startDate = today
          }
          return fetchDailySalesReceiptContent(
            {
              pageIndex: currentPage - 1,
              pageSize,
              status:
                filters.status === '-1' ? undefined : Number(filters.status),
              startDate,
              endDate,
              token: filters.token?.trim() || undefined,
            },
            mode,
          )
        }}
      />
    </PageContainer>
  )
}
